// Third party library imports
import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { lastValueFrom } from 'rxjs';

// Zopup library imports
import {
    BooleanWebApiResponse,
    TenantDto,
    UserCreateCommand,
    UserDto,
    UserDtoWebApiResponse,
    UserEmailInvitationCommand,
    UserPagedListQuery,
} from '@zopup/zop-types';
import { StorageKey, StorageService } from '@zopup/zop-shared';

// Internal project imports
import { UserService } from './user.service';
import { serializeQuery } from '../shared/query-cache.util';

const PAGE_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const ENTITY_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
const DEFAULT_PAGE_SIZE = 250;

type UserEntity = UserDto;

type UserRepositoryPage = {
    ids: string[];
    totalCount: number;
    cachedAt: number;
    query: UserPagedListQuery;
};

export type UserState = {
    entities: Record<string, UserEntity>;
    entityMeta: Record<string, number>;
    pageCache: Record<string, UserRepositoryPage>;
};

const userInitialState: UserState = {
    entities: {},
    entityMeta: {},
    pageCache: {},
};

const ensureQueryDefaults = (
    query: UserPagedListQuery
): Required<Pick<UserPagedListQuery, 'pageNumber' | 'pageSize'>> &
    UserPagedListQuery => ({
    ...query,
    pageNumber: query.pageNumber ?? 1,
    pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
});

const mergeUser = (
    existing: UserEntity | undefined,
    incoming: UserEntity
): UserEntity => {
    if (!existing) {
        return { ...incoming };
    }

    let changed = false;
    const merged = { ...existing };

    Object.entries(incoming as any).forEach(([key, value]) => {
        if (value === undefined) {
            return;
        }
        if ((merged as any)[key] !== value) {
            (merged as any)[key] = value;
            changed = true;
        }
    });

    return changed ? merged : existing;
};

export const UserStore = signalStore(
    { providedIn: 'root' },
    withState(userInitialState),
    withMethods(
        (
            store,
            userService = inject(UserService),
            storageService = inject(StorageService)
        ) => {
            const getTenantId = (): string => {
                const tenantId = storageService.getItem<TenantDto>(
                    StorageKey.tenant
                )?.id;
                if (!tenantId) {
                    throw new Error('Tenant id is not available');
                }
                return tenantId;
            };

            const upsert = (users: UserEntity[]): void => {
                if (!users.length) {
                    return;
                }

                patchState(store, (state) => {
                    let entities = state.entities;
                    let entityMeta = state.entityMeta;
                    let entitiesChanged = false;
                    let metaChanged = false;
                    const now = Date.now();

                    for (const user of users) {
                        const id = user?.id?.toString();
                        if (!id) {
                            continue;
                        }

                        if (!entitiesChanged) {
                            entities = { ...entities };
                            entitiesChanged = true;
                        }

                        const merged = mergeUser(entities[id], user);
                        if (merged !== entities[id]) {
                            entities[id] = merged;
                        }

                        if (!metaChanged) {
                            entityMeta = { ...entityMeta };
                            metaChanged = true;
                        }
                        entityMeta[id] = now;
                    }

                    const next: Partial<UserState> = {};
                    if (entitiesChanged) {
                        next.entities = entities;
                    }
                    if (metaChanged) {
                        next.entityMeta = entityMeta;
                    }
                    return Object.keys(next).length ? next : {};
                });
            };

            const invalidatePageCache = (
                predicate?: (entry: UserRepositoryPage, key: string) => boolean
            ): void => {
                patchState(store, (state) => {
                    const entries = Object.entries(state.pageCache);
                    if (!entries.length) {
                        return {};
                    }

                    let changed = false;
                    const nextCache: Record<string, UserRepositoryPage> = {};

                    for (const [key, entry] of entries) {
                        if (predicate && predicate(entry, key)) {
                            changed = true;
                            continue;
                        }
                        nextCache[key] = entry;
                    }

                    if (!changed) {
                        return {};
                    }

                    return { pageCache: nextCache };
                });
            };

            const clearPageCache = (): void => {
                patchState(store, (state) =>
                    Object.keys(state.pageCache).length
                        ? { pageCache: {} }
                        : {}
                );
            };

            const resolveCachedPage = (
                query: UserPagedListQuery
            ): { rows: UserEntity[]; totalCount: number } | null => {
                const normalized = ensureQueryDefaults(query);
                const cacheKey = serializeQuery(
                    normalized as unknown as Record<string, unknown>
                );
                const entry = store.pageCache()[cacheKey];
                if (!entry) {
                    return null;
                }
                if (Date.now() - entry.cachedAt > PAGE_CACHE_TTL_MS) {
                    invalidatePageCache((_, key) => key === cacheKey);
                    return null;
                }

                const entities = store.entities();
                const rows = entry.ids
                    .map((id) => entities[id])
                    .filter((user): user is UserEntity => !!user);

                if (rows.length !== entry.ids.length) {
                    invalidatePageCache((_, key) => key === cacheKey);
                    return null;
                }

                return { rows, totalCount: entry.totalCount };
            };

            const fetchPage = async (
                query: UserPagedListQuery,
                options?: { force?: boolean }
            ): Promise<{ rows: UserEntity[]; totalCount: number }> => {
                if (!options?.force) {
                    const cached = resolveCachedPage(query);
                    if (cached) {
                        return cached;
                    }
                }

                const normalized = ensureQueryDefaults(query);
                const cacheKey = serializeQuery(
                    normalized as unknown as Record<string, unknown>
                );

                const response = await lastValueFrom(
                    userService.userGet(
                        normalized.email,
                        normalized.phoneNumber,
                        normalized.mobileNumber,
                        normalized.isActive,
                        normalized.orderBy,
                        normalized.isOrderByDescending,
                        normalized.searchText,
                        normalized.pageNumber,
                        normalized.pageSize
                    )
                );

                if (!response.isSuccess) {
                    const message =
                        response.errors?.[0]?.message ||
                        'Failed to fetch users';
                    throw new Error(message);
                }

                const users = (response.result ?? []) as UserEntity[];
                upsert(users);

                patchState(store, (state) => {
                    const nextCache = {
                        ...state.pageCache,
                        [cacheKey]: {
                            ids: users
                                .map((user) => user?.id?.toString())
                                .filter((id): id is string => !!id),
                            totalCount: users.length,
                            cachedAt: Date.now(),
                            query: normalized,
                        },
                    } satisfies Record<string, UserRepositoryPage>;

                    return { pageCache: nextCache };
                });

                const rows = users.map((user) => store.entities()[user.id!.toString()]);
                return {
                    rows,
                    totalCount: users.length,
                };
            };

            const fetchById = async (
                id: string,
                options?: { force?: boolean }
            ): Promise<UserEntity | null> => {
                if (!id) {
                    return null;
                }

                if (!options?.force) {
                    const cached = store.entities()[id];
                    const cachedAt = store.entityMeta()[id];
                    if (
                        cached &&
                        cachedAt &&
                        Date.now() - cachedAt < ENTITY_CACHE_TTL_MS
                    ) {
                        return cached;
                    }
                }

                const response = await lastValueFrom(
                    userService.userSingleGet(undefined, id)
                );

                if (!response.isSuccess || !response.result) {
                    const message =
                        response.errors?.[0]?.message ||
                        'User not found';
                    throw new Error(message);
                }

                upsert([response.result]);
                return store.entities()[id] ?? response.result;
            };

            const add = async (
                command: UserCreateCommand
            ): Promise<UserDtoWebApiResponse> => {
                const response = await lastValueFrom(
                    userService.userPost(command)
                );

                if (response.isSuccess) {
                    if (response.result) {
                        upsert([response.result]);
                    }
                    clearPageCache();
                }

                return response as UserDtoWebApiResponse;
            };

            const update = async (
                command: UserCreateCommand
            ): Promise<UserDtoWebApiResponse> => {
                const response = await lastValueFrom(
                    userService.userPut(command)
                );

                if (response.isSuccess) {
                    if (response.result) {
                        upsert([response.result]);
                    } else if (command.userDetails?.id) {
                        // Ensure updated entity is re-fetched next time
                        patchState(store, (state) => {
                            const userId = command.userDetails!.id!.toString();
                            if (!state.entityMeta[userId]) {
                                return {};
                            }
                            const nextMeta = { ...state.entityMeta };
                            delete nextMeta[userId];
                            return { entityMeta: nextMeta };
                        });
                    }
                    invalidatePageCache((entry) =>
                        entry.ids.includes(command.userDetails?.id?.toString() || '')
                    );
                }

                return response as UserDtoWebApiResponse;
            };

            const sendEmailInvitation = async (
                command: UserEmailInvitationCommand
            ): Promise<BooleanWebApiResponse> => {
                const response = await lastValueFrom(
                    userService.userSendEmailInvitationPut(command)
                );

                return response as BooleanWebApiResponse;
            };

            return {
                fetchPage,
                fetchById,
                getCachedPage: resolveCachedPage,
                add,
                update,
                sendEmailInvitation,
                invalidatePageCache,
                clearPageCache,
            };
        }
    )
);
